import { hasAdminProfile } from '../../utils/profileRole';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { 
  Box, 
  Button, 
  Chip, 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  Grid, 
  IconButton, 
  MenuItem, 
  Select, 
  TextField, 
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { CloseSharp, Visibility } from '@material-ui/icons';
import { FilterAltOffSharp, FilterAltSharp } from '@mui/icons-material';
import TableInfo from '../../components/TableInfo';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import FilterSlidePanel from '../../components/FilterSlidePanel';
import { 
  getUserActivityLogs, 
  getUserActivityLogStatistics,
  selectUserActivityLogs,
  selectUserActivityLogLoading,
  selectUserActivityLogError,
  selectUserActivityLogStatistics
} from '../../redux/userActivityLog/userActivityLog.actions';

const UserActivityLogsPage = props => {
  const { theme } = useContext(ThemeContext);
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    user_id: null,
    action_type: '',
    entity_type: '',
    limit: 50,
    offset: 0
  });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const { logs, loading, error, total } = props;
  const { statistics } = props;

  const formatIpForDisplay = (ip) => {
    if (!ip) return '---';
    if (ip === '::1' || ip === '127.0.0.1') return `localhost (${ip})`;
    return ip;
  };

  const fieldLabels = {
    name: 'Nome',
    email: 'E-mail',
    title: 'Título',
    status: 'Status',
    priority: 'Prioridade',
    profile_id: 'Perfil',
    service_group_id: 'Grupo de serviço',
    picture: 'Foto',
    CRM_id: 'CRM ID',
    created_by: 'Criado por',
    assigned_to: 'Atribuído a',
    is_internal: 'Interno',
    client: 'Cliente',
    client_id: 'Cliente (ID)',
    company_id: 'Empresa (ID)',
    category: 'Categoria',
    contact: 'Contato',
    channel: 'Canal',
    cc_users: 'CC (cópia)',
    created_at: 'Criado em',
    updated_at: 'Atualizado em',
    ticket_id: 'ID do ticket',
    ticket_title: 'Título do ticket',
    created_by_name: 'Criado por (nome)',
  };

  const valueLabels = {
    status: { 0: 'Offline', 1: 'Online', 2: 'Online', 3: 'Ausente' },
    profile_id: { 1: 'Admin', 2: 'Usuário' },
    priority: { 0: 'Baixa', 1: 'Média', 2: 'Alta', 3: 'Urgente' },
  };

  const dateTimeKeys = ['created_at', 'updated_at', 'sync_date', 'date'];
  const formatDateTime = (value) => {
    if (value == null) return '—';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return format(d, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const formatValueForDisplay = (value, key) => {
    if (value === null || value === undefined) return '—';
    if (key && valueLabels[key]) {
      const label = valueLabels[key][value];
      if (label !== undefined) return label;
    }
    if (key && dateTimeKeys.includes(key)) return formatDateTime(value);
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|\s)/.test(value)) return formatDateTime(value);
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'Nenhum';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const renderValuePairs = (obj, bgColor) => {
    if (!obj || typeof obj !== 'object' || Object.keys(obj).length === 0) return null;
    const keysToHide = ['password', 'remember_token', 'updated_at'];
    const entries = Object.entries(obj).filter(([key]) => !keysToHide.includes(key));
    if (entries.length === 0) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          p: 2,
          borderRadius: 2,
          backgroundColor: bgColor || theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {entries.map(([key, value]) => (
          <Box
            key={key}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              gap: 1,
              py: 0.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ minWidth: '140px' }}>
              {fieldLabels[key] || key}:
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
              {formatValueForDisplay(value, key)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const getChangedFields = (oldObj, newObj) => {
    if (!oldObj || !newObj || typeof oldObj !== 'object' || typeof newObj !== 'object') return {};
    const keysToHide = ['password', 'remember_token', 'updated_at'];
    const allKeys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])].filter(k => !keysToHide.includes(k));
    const changed = {};
    allKeys.forEach(key => {
      const oldVal = oldObj[key];
      const newVal = newObj[key];
      const oldStr = JSON.stringify(oldVal);
      const newStr = JSON.stringify(newVal);
      if (oldStr !== newStr) changed[key] = { old: oldVal, new: newVal };
    });
    return changed;
  };

  const renderChangedOnly = (oldValues, newValues, bgColor) => {
    const changed = getChangedFields(oldValues, newValues);
    if (Object.keys(changed).length === 0) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          p: 2,
          borderRadius: 2,
          backgroundColor: bgColor || theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {Object.entries(changed).map(([key, { old: oldVal, new: newVal }]) => {
          const onlyFinalValue = ['updated_at', 'created_at'].includes(key);
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 0.5,
                py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-of-type': { borderBottom: 'none' },
              }}
            >
              <Typography variant="caption" fontWeight="600" color="text.secondary" sx={{ minWidth: '140px' }}>
                {fieldLabels[key] || key}
              </Typography>
              {onlyFinalValue ? (
                <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
                  {formatValueForDisplay(newVal, key)}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" component="span" sx={{ wordBreak: 'break-word' }}>
                    {formatValueForDisplay(oldVal, key)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>→</Typography>
                  <Typography variant="body2" component="span" fontWeight="500" sx={{ wordBreak: 'break-word' }}>
                    {formatValueForDisplay(newVal, key)}
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const createRows = useCallback(() => {
    if (!logs || !Array.isArray(logs)) {
      setRows([]);
      return;
    }

    const newRows = logs.map((log) => {
      const dateFormat = (date) => {
        if (!date) return '---';
        return format(new Date(date), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
      };

      return {
        id: log.id,
        user: log.user ? log.user.name : 'Usuário não encontrado',
        userEmail: log.user ? log.user.email : '---',
        actionType: log.action_type,
        actionDescription: log.action_description,
        entityType: log.entity_type || '---',
        entityId: log.entity_id || '---',
        createdAt: dateFormat(log.created_at),
        ipAddress: formatIpForDisplay(log.ip_address),
        userAgent: log.user_agent || '---',
        oldValues: log.old_values,
        newValues: log.new_values,
        metadata: log.metadata
      };
    });

    setRows(newRows);
  }, [logs]);

  useEffect(() => {
    if (!hasAdminProfile(user)) {
      return;
    }
    props.getUserActivityLogs(filters);
    props.getUserActivityLogStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    createRows();
  }, [createRows]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    props.getUserActivityLogs({ ...filters, offset: 0 });
    setPage(0);
    setFilterPanelOpen(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      user_id: null,
      action_type: '',
      entity_type: '',
      limit: 50,
      offset: 0
    };
    setFilters(clearedFilters);
    props.getUserActivityLogs(clearedFilters);
    setPage(0);
  };

  const handleOpenDetailDialog = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedLog(null);
  };


  const getActionTypeColor = (actionType) => {
    const colors = {
      'login': 'success',
      'logout': 'info',
      'create': 'primary',
      'update': 'warning',
      'delete': 'error',
      'view': 'default'
    };
    return colors[actionType] || 'default';
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'login': 'Login',
      'logout': 'Logout',
      'create': 'Criar',
      'update': 'Atualizar',
      'delete': 'Excluir',
      'view': 'Visualizar'
    };
    return labels[actionType] || actionType;
  };

  if (!hasAdminProfile(user)) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: '100vh' }}>
        <Alert severity="error">
          Acesso negado. Apenas administradores podem visualizar os logs de atividades.
        </Alert>
      </Grid>
    );
  }

  const columns = [
    {
      field: 'createdAt',
      headerName: 'Data/Hora',
      editable: false,
      flex: 1.5,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'user',
      headerName: 'Usuário',
      editable: false,
      flex: 1.5,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {params.row.user}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.userEmail}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actionType',
      headerName: 'Ação',
      editable: false,
      flex: 1,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Chip
          label={getActionTypeLabel(params.row.actionType)}
          color={getActionTypeColor(params.row.actionType)}
          size="small"
        />
      ),
    },
    {
      field: 'actionDescription',
      headerName: 'Descrição',
      editable: false,
      flex: 2,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'entityType',
      headerName: 'Entidade',
      editable: false,
      flex: 1,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">
            {params.row.entityType}
          </Typography>
          {params.row.entityId !== '---' && (
            <Typography variant="caption" color="textSecondary">
              ID: {params.row.entityId}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'ipAddress',
      headerName: 'IP',
      editable: false,
      flex: 1,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'details',
      headerName: 'Detalhes',
      editable: false,
      flex: 1,
      headerClassName: 'table-sort-header compact',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Visibility />}
          onClick={() => handleOpenDetailDialog(params.row)}
        >
          Ver
        </Button>
      ),
    },
  ];

  return (
    <Grid className='user-activity-logs-page' sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Grid container wrap='nowrap' sx={{ padding: '8px 16px', mb: 2 }}>
        <Grid item xs={12} container 
          spacing={2} 
          wrap='nowrap' 
          alignItems="center"
          justifyContent="space-between"
          sx={{ backgroundColor: 'transparent', minHeight: '64px' }}>
          
          <Grid item>
            <Typography variant="h5" fontWeight="bold">
              Logs de Atividades
            </Typography>
            {statistics && (
              <Typography variant="caption" color="textSecondary">
                Total: {statistics.total_activities || total} atividades registradas
              </Typography>
            )}
          </Grid>

          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={(filters.user_id || filters.action_type || filters.entity_type) ? <FilterAltSharp /> : <FilterAltOffSharp />}
              onClick={() => setFilterPanelOpen(true)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 3
              }}
            >
              Filtros
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <TableInfo
              rows={rows}
              columns={columns}
              isBorder={true}
              fullHeight={true}
              autoHeight={false}
              color={theme.palette.background.default}
            />
          </Box>
          {total > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="body2" color="textSecondary">
                Mostrando {rows.length} de {total} registros
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page === 0}
                  onClick={() => {
                    const newPage = page - 1;
                    setPage(newPage);
                    props.getUserActivityLogs({ ...filters, offset: newPage * rowsPerPage });
                  }}
                >
                  Anterior
                </Button>
                <Typography variant="body2">
                  Página {page + 1} de {Math.ceil(total / rowsPerPage)}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={page >= Math.ceil(total / rowsPerPage) - 1}
                  onClick={() => {
                    const newPage = page + 1;
                    setPage(newPage);
                    props.getUserActivityLogs({ ...filters, offset: newPage * rowsPerPage });
                  }}
                >
                  Próxima
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}

      <Dialog
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: theme.shadows[12],
            backgroundColor: theme.palette.background.paper,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 2,
            px: 3,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="600">
              Detalhes do Log
            </Typography>
            <IconButton
              onClick={handleCloseDetailDialog}
              size="small"
              sx={{
                '&:hover': { backgroundColor: theme.palette.action.hover },
              }}
            >
              <CloseSharp fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            backgroundColor: theme.palette.background.default,
            p: 3,
            pt: 5,
          }}
        >
          {selectedLog && (
            <Box sx={{ pt: 1 }}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                    Usuário
                  </Typography>
                  <Typography variant="body2">{selectedLog.user}</Typography>
                  <Typography variant="caption" color="text.secondary">{selectedLog.userEmail}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                    Data/Hora
                  </Typography>
                  <Typography variant="body2">{selectedLog.createdAt}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                    Tipo de Ação
                  </Typography>
                  <Chip
                    label={getActionTypeLabel(selectedLog.actionType)}
                    color={getActionTypeColor(selectedLog.actionType)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                    IP
                  </Typography>
                  <Typography variant="body2">{selectedLog.ipAddress}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                    Descrição
                  </Typography>
                  <Typography variant="body2">{selectedLog.actionDescription}</Typography>
                </Grid>
                {selectedLog.entityType !== '---' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                        Tipo de Entidade
                      </Typography>
                      <Typography variant="body2">{selectedLog.entityType}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                        ID da Entidade
                      </Typography>
                      <Typography variant="body2">{selectedLog.entityId}</Typography>
                    </Grid>
                  </>
                )}
                {selectedLog.oldValues && selectedLog.newValues &&
                 Object.keys(selectedLog.oldValues).length > 0 && Object.keys(selectedLog.newValues).length > 0 &&
                 Object.keys(getChangedFields(selectedLog.oldValues, selectedLog.newValues)).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      Alterações
                    </Typography>
                    {renderChangedOnly(selectedLog.oldValues, selectedLog.newValues, theme.palette.background.paper)}
                  </Grid>
                )}
                {selectedLog.oldValues && (!selectedLog.newValues || Object.keys(selectedLog.newValues).length === 0) &&
                 Object.keys(selectedLog.oldValues).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      Valores removidos
                    </Typography>
                    {renderValuePairs(selectedLog.oldValues, theme.palette.background.paper)}
                  </Grid>
                )}
                {selectedLog.newValues && (!selectedLog.oldValues || Object.keys(selectedLog.oldValues).length === 0) &&
                 Object.keys(selectedLog.newValues).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      Valores criados
                    </Typography>
                    {renderValuePairs(selectedLog.newValues, theme.palette.background.paper)}
                  </Grid>
                )}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                      Metadados
                    </Typography>
                    {renderValuePairs(selectedLog.metadata, theme.palette.background.paper)}
                  </Grid>
                )}
                {selectedLog.userAgent && (
                  <Grid item xs={12}>
                    <Typography variant="caption" fontWeight="bold" color="text.secondary" display="block" gutterBottom>
                      User Agent
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word', opacity: 0.9 }}>
                      {selectedLog.userAgent}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <FilterSlidePanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        title="Filtros de Logs"
        onApply={handleApplyFilters}
        onCancel={handleClearFilters}
        selectedFilter={filters.user_id || filters.action_type || filters.entity_type}
        applyButtonText="Aplicar Filtros"
        cancelButtonText="Limpar Filtros"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Usuário
            </Typography>
            <Select
              value={filters.user_id || ''}
              onChange={(e) => handleFilterChange('user_id', e.target.value || null)}
              fullWidth
              size="small"
              displayEmpty
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              <MenuItem value="">Todos os usuários</MenuItem>
              {props.users && props.users.map((currentUser) => (
                <MenuItem key={currentUser.id} value={currentUser.id}>
                  {currentUser.name} ({currentUser.email})
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Tipo de Ação
            </Typography>
            <Select
              value={filters.action_type}
              onChange={(e) => handleFilterChange('action_type', e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              <MenuItem value="">Todas as ações</MenuItem>
              <MenuItem value="login">Login</MenuItem>
              <MenuItem value="logout">Logout</MenuItem>
              <MenuItem value="create">Criar</MenuItem>
              <MenuItem value="update">Atualizar</MenuItem>
              <MenuItem value="delete">Excluir</MenuItem>
              <MenuItem value="view">Visualizar</MenuItem>
            </Select>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Tipo de Entidade
            </Typography>
            <Select
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              fullWidth
              size="small"
              displayEmpty
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
              }}
            >
              <MenuItem value="">Todas as entidades</MenuItem>
              <MenuItem value="User">Usuário</MenuItem>
              <MenuItem value="Ticket">Ticket</MenuItem>
              <MenuItem value="Client">Cliente</MenuItem>
              <MenuItem value="Company">Empresa</MenuItem>
              <MenuItem value="ServiceGroup">Grupo de Serviço</MenuItem>
              <MenuItem value="Profile">Perfil</MenuItem>
            </Select>
          </Box>
        </Box>
      </FilterSlidePanel>
    </Grid>
  );
};

const mapStateToProps = state => {
  const { user } = state;
  const { users } = user || {};
  
  return {
    logs: selectUserActivityLogs(state) || [],
    loading: selectUserActivityLogLoading(state) || false,
    error: selectUserActivityLogError(state),
    statistics: selectUserActivityLogStatistics(state),
    total: state.userActivityLogs?.total || 0,
    users: users || []
  };
};

const mapDispatchToProps = dispatch => ({
  getUserActivityLogs: (filters) => dispatch(getUserActivityLogs(filters)),
  getUserActivityLogStatistics: (filters) => dispatch(getUserActivityLogStatistics(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserActivityLogsPage);
